// ============================================
// FILE: hostellogin.js
// Complete Authentication System with Forgot Password
// Supports: Student | Admin | SuperAdmin | Mess Manager | Owner
// Methods: Phone OTP | Email/Password | Forgot Password with OTP
// UPDATED: Added Forgot Password Feature with OTP Verification
// ============================================

import React, { useState, useEffect } from 'react';

function ResponsiveLoginPage({ onNavigate }) {
  // ==========================================
  // STATE MANAGEMENT
  // ==========================================
  const [isMobile, setIsMobile] = useState(false);
  const [authMethod, setAuthMethod] = useState('phone'); // 'phone' | 'email'
  const [step, setStep] = useState('initial'); // 'initial' | 'otp-verification' | 'forgot-password' | 'forgot-otp-verification' | 'reset-password' | 'success'

  // Form Fields
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Forgot Password Fields
  const [forgotPhoneNumber, setForgotPhoneNumber] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  // API Configuration
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in/api';

  // ==========================================
  // RESPONSIVE CHECK
  // ==========================================
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // ==========================================
  // RESEND OTP TIMER
  // ==========================================
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // ==========================================
  // UTILITY FUNCTIONS
  // ==========================================

  const clearError = () => setError('');

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 6;
  };

  const handleRoleBasedRedirect = (userRole) => {
    console.log(`🔐 Redirecting user with role: ${userRole}`);

    const roleRoutes = {
      'hostel-superadmin': 'hostel-superadmin-dashboard',
      'hostel-admin': 'hostel-admin-dashboard',
      'hostel-student': 'hostel-student-dashboard',
      'hostel-messmanager': 'hostel-mess-dashboard',
      'hostel-owner': 'hostel-owner-dashboard'
    };

    const targetRoute = roleRoutes[userRole];
    
    if (!targetRoute) {
      console.error(`❌ Unknown user role: ${userRole}. Cannot redirect.`);
      setError(`Unknown user role: ${userRole}. Please contact administrator.`);
      return;
    }
    
    console.log(`✅ Redirecting to: ${targetRoute}`);

    setTimeout(() => {
      onNavigate(targetRoute);
    }, 1500);
  };

  const storeUserSession = (userData) => {
    const userInfo = {
      userId: userData.userId,
      role: userData.userRole,
      name: userData.fullName,
      phone: userData.phoneNumber,
      email: userData.email
    };

    // Generate a token for AuthContext compatibility
    const token = userData.token || `token_${userData.userId}_${Date.now()}`;

    // Store in both session and local storage
    sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('userInfo', JSON.stringify(userInfo));
    localStorage.setItem('token', token);
    localStorage.setItem('isLoggedIn', 'true');

    console.log('✅ Token stored:', token);
    console.log('✅ User session stored:', userInfo);
    return userInfo;
  };

  const createUserSession = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userId,
          ipAddress: 'client-ip'
        })
      });

      if (response.ok) {
        const sessionData = await response.json();
        if (sessionData.success && sessionData.sessionToken) {
          sessionStorage.setItem('sessionToken', sessionData.sessionToken);
          localStorage.setItem('sessionToken', sessionData.sessionToken);
          console.log('✅ Session token created');
        }
      }
    } catch (error) {
      console.warn('⚠️ Session creation failed (non-critical):', error);
    }
  };

  // ==========================================
  // PHONE OTP AUTHENTICATION
  // ==========================================

  const handleSendOTP = async () => {
    if (!phoneNumber) {
      setError('Please enter your phone number');
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      setError('Please enter a valid 10-digit phone number starting with 6-9');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStep('otp-verification');
        setSuccessMessage('OTP sent successfully to your WhatsApp! 📱');
        setResendTimer(60);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        if (data.message && data.message.includes('not registered')) {
          setError(
            <div style={{ textAlign: 'left' }}>
              <strong>❌ Phone number not registered!</strong>
              <p style={{ margin: '8px 0', fontSize: '13px', color: '#666' }}>
                This number is not in our system. Please register first to access the hostel portal.
              </p>
              <button
                onClick={() => onNavigate('student-register')}
                style={{
                  marginTop: '8px',
                  padding: '10px 16px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  width: '100%',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
              >
                📝 Register Now →
              </button>
            </div>
          );
        } else if (data.message && data.message.includes('Account inactive')) {
          setError('❌ Account inactive. Please contact administrator.');
        } else {
          setError(data.message || '❌ Failed to send OTP. Please try again.');
        }
      }
    } catch (error) {
      console.error('❌ OTP Send Error:', error);
      setError('❌ Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp) {
      setError('Please enter the OTP code');
      return;
    }

    if (otp.length !== 6) {
      setError('OTP must be 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber,
          otp
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ OTP Verification successful:', data);
        
        // Store user session with correct data structure
        const userInfo = storeUserSession(data.user);
        
        // Create session token
        await createUserSession(data.user.userId);
        
        setSuccessMessage('✅ Login successful! Redirecting...');
        setStep('success');
        
        // Redirect based on role
        handleRoleBasedRedirect(data.user.userRole);
      } else {
        setError(data.message || '❌ Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('❌ OTP Verification Error:', error);
      setError('❌ Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // EMAIL/PASSWORD AUTHENTICATION
  // ==========================================

  const handleEmailPasswordLogin = async () => {
    // Validation
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!validatePassword(password)) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: email, password })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Store user session
        storeUserSession(data.user);
        
        // Create server session
        await createUserSession(data.user.userId);
        
        // Show success and redirect
        setStep('success');
        setSuccessMessage(`✅ Welcome back, ${data.user.fullName}!`);
        
        // Redirect based on role
        handleRoleBasedRedirect(data.user.userRole);
      } else {
        // Handle specific error cases
        if (data.message && (data.message.includes('not found') || data.message.includes('Invalid credentials'))) {
          setError(
            <div style={{ textAlign: 'left' }}>
              <strong>❌ Login Failed</strong>
              <p style={{ margin: '8px 0', fontSize: '13px', color: '#666' }}>
                {data.message.includes('not found') 
                  ? 'This email is not registered in our system.'
                  : 'Invalid email or password. Please check your credentials.'}
              </p>
              <button
                onClick={() => onNavigate('student-register')}
                style={{
                  marginTop: '8px',
                  padding: '10px 16px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  width: '100%',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
              >
                📝 Register Now
              </button>
            </div>
          );
        } else {
          setError(data.message || '❌ Login failed. Please try again.');
        }
      }
    } catch (err) {
      console.error('Login Error:', err);
      setError('🔌 Network error. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // FORGOT PASSWORD FUNCTIONALITY
  // ==========================================

  const handleForgotPassword = () => {
    setStep('forgot-password');
    setError('');
    setSuccessMessage('');
    setForgotPhoneNumber('');
    setForgotOtp('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSendForgotPasswordOTP = async () => {
    if (!forgotPhoneNumber) {
      setError('Please enter your phone number');
      return;
    }

    if (!validatePhoneNumber(forgotPhoneNumber)) {
      setError('Please enter a valid 10-digit phone number starting with 6-9');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: forgotPhoneNumber })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStep('forgot-otp-verification');
        setSuccessMessage('OTP sent successfully to your WhatsApp! 📱');
        setResendTimer(60);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        if (data.message && data.message.includes('not registered')) {
          setError('❌ Phone number not registered. Please check and try again.');
        } else if (data.message && data.message.includes('Account inactive')) {
          setError('❌ Account inactive. Please contact administrator.');
        } else {
          setError(data.message || '❌ Failed to send OTP. Please try again.');
        }
      }
    } catch (error) {
      console.error('❌ Forgot Password OTP Error:', error);
      setError('❌ Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyForgotPasswordOTP = async () => {
    if (!forgotOtp) {
      setError('Please enter the OTP code');
      return;
    }

    if (forgotOtp.length !== 6) {
      setError('OTP must be 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber: forgotPhoneNumber,
          otp: forgotOtp
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStep('reset-password');
        setSuccessMessage('✅ OTP verified! Now set your new password.');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(data.message || '❌ Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('❌ OTP Verification Error:', error);
      setError('❌ Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      setError('Please enter both password fields');
      return;
    }

    if (!validatePassword(newPassword)) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber: forgotPhoneNumber,
          newPassword: newPassword
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccessMessage('✅ Password reset successful! Redirecting to login...');
        setTimeout(() => {
          handleBackToInitial();
        }, 2000);
      } else {
        setError(data.message || '❌ Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('❌ Password Reset Error:', error);
      setError('❌ Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // NAVIGATION HANDLERS
  // ==========================================

  const handleSwitchToEmail = () => {
    setAuthMethod('email');
    setError('');
    setSuccessMessage('');
    setPhoneNumber('');
    setOtp('');
  };

  const handleSwitchToPhone = () => {
    setAuthMethod('phone');
    setError('');
    setSuccessMessage('');
    setEmail('');
    setPassword('');
  };

  const handleBackToInitial = () => {
    setStep('initial');
    setError('');
    setSuccessMessage('');
    setOtp('');
    setForgotPhoneNumber('');
    setForgotOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setResendTimer(0);
  };

  const handleGoToRegister = () => {
    if (onNavigate) {
      onNavigate('student-register');
    }
  };

  const handleCheckApplicationStatus = () => {
    if (onNavigate) {
      onNavigate('application-status');
    }
  };

  const handleGoToHome = () => {
    if (onNavigate) {
      onNavigate('home');
    }
  };

  const handleKeyPress = (e, callback) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      callback();
    }
  };

  // ==========================================
  // STYLES
  // ==========================================

  const mobileStyles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #2b6777 0%, #52ab98 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    loginBox: {
      backgroundColor: '#ffffff',
      borderRadius: '24px',
      padding: '36px 28px',
      width: '100%',
      maxWidth: '420px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
    },
    heading: {
      fontSize: '28px',
      fontWeight: '900',
      color: '#1f2937',
      marginBottom: '8px',
      textAlign: 'center',
      background: 'linear-gradient(135deg, #2b6777 0%, #52ab98 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    subtitle: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '32px',
      textAlign: 'center',
      fontWeight: '500',
    },
    formGroup: {
      marginBottom: '24px',
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '700',
      color: '#374151',
      marginBottom: '10px',
    },
    input: {
      width: '100%',
      padding: '14px 16px',
      fontSize: '15px',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      transition: 'all 0.3s ease',
      fontFamily: 'inherit',
      backgroundColor: '#ffffff',
      color: '#1f2937',
    },
    button: {
      width: '100%',
      padding: '16px',
      fontSize: '16px',
      fontWeight: '700',
      color: '#ffffff',
      background: 'linear-gradient(135deg, #2b6777 0%, #52ab98 100%)',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 20px rgba(43, 103, 119, 0.3)',
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    errorMessage: {
      backgroundColor: '#fee2e2',
      color: '#dc2626',
      padding: '14px 16px',
      borderRadius: '12px',
      marginBottom: '20px',
      fontSize: '14px',
      fontWeight: '600',
      border: '2px solid #fecaca',
    },
    successMessage: {
      backgroundColor: '#d1fae5',
      color: '#059669',
      padding: '14px 16px',
      borderRadius: '12px',
      marginBottom: '20px',
      fontSize: '14px',
      fontWeight: '600',
      border: '2px solid #a7f3d0',
    },
    linkText: {
      textAlign: 'center',
      fontSize: '14px',
      color: '#6b7280',
      marginTop: '20px',
      fontWeight: '500',
    },
    link: {
      color: '#2b6777',
      fontWeight: '700',
      cursor: 'pointer',
      textDecoration: 'none',
      transition: 'color 0.3s ease',
    },
    divider: {
      display: 'flex',
      alignItems: 'center',
      margin: '24px 0',
    },
    dividerLine: {
      flex: 1,
      height: '1px',
      backgroundColor: '#e5e7eb',
    },
    dividerText: {
      padding: '0 16px',
      fontSize: '13px',
      fontWeight: '600',
      color: '#9ca3af',
    },
    socialButton: {
      width: '100%',
      padding: '14px 20px',
      fontSize: '15px',
      fontWeight: '600',
      color: '#374151',
      backgroundColor: '#ffffff',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      marginBottom: '12px',
    },
    passwordContainer: {
      position: 'relative',
    },
    eyeIcon: {
      position: 'absolute',
      right: '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      cursor: 'pointer',
      fontSize: '20px',
      color: '#6b7280',
      userSelect: 'none',
    },
    backButton: {
      background: 'transparent',
      border: 'none',
      color: '#2b6777',
      fontSize: '15px',
      fontWeight: '700',
      cursor: 'pointer',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 0',
    },
    homeButton: {
      position: 'absolute',
      top: '20px',
      left: '20px',
      padding: '10px 20px',
      background: 'rgba(43,103,119,0.1)',
      border: '2px solid rgba(82,171,152,0.3)',
      borderRadius: '10px',
      color: '#2b6777',
      fontSize: '14px',
      fontWeight: '700',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.3s ease',
      zIndex: 10,
    },
  };

  const desktopStyles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    leftSide: {
      flex: '1',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 40px',
      backgroundColor: '#ffffff',
      position: 'relative',
      overflow: 'hidden',
    },
    leftContent: {
      width: '100%',
      maxWidth: '480px',
      position: 'relative',
      zIndex: 2,
    },
    heading: {
      fontSize: '36px',
      fontWeight: '900',
      color: '#1f2937',
      marginBottom: '12px',
      background: 'linear-gradient(135deg, #2b6777 0%, #52ab98 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    subtitle: {
      fontSize: '16px',
      color: '#6b7280',
      marginBottom: '36px',
      fontWeight: '500',
    },
    formGroup: {
      marginBottom: '24px',
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '700',
      color: '#374151',
      marginBottom: '10px',
    },
    input: {
      width: '100%',
      padding: '14px 18px',
      fontSize: '15px',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      transition: 'all 0.3s ease',
      fontFamily: 'inherit',
      backgroundColor: '#ffffff',
      color: '#1f2937',
    },
    button: {
      width: '100%',
      padding: '16px',
      fontSize: '16px',
      fontWeight: '700',
      color: '#ffffff',
      background: 'linear-gradient(135deg, #2b6777 0%, #52ab98 100%)',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 20px rgba(43, 103, 119, 0.3)',
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    errorMessage: {
      backgroundColor: '#fee2e2',
      color: '#dc2626',
      padding: '14px 18px',
      borderRadius: '12px',
      marginBottom: '20px',
      fontSize: '14px',
      fontWeight: '600',
      border: '2px solid #fecaca',
    },
    successMessage: {
      backgroundColor: '#d1fae5',
      color: '#059669',
      padding: '14px 18px',
      borderRadius: '12px',
      marginBottom: '20px',
      fontSize: '14px',
      fontWeight: '600',
      border: '2px solid #a7f3d0',
    },
    linkText: {
      textAlign: 'center',
      fontSize: '14px',
      color: '#6b7280',
      marginTop: '20px',
      fontWeight: '500',
    },
    link: {
      color: '#2b6777',
      fontWeight: '700',
      cursor: 'pointer',
      textDecoration: 'none',
      transition: 'color 0.3s ease',
    },
    divider: {
      display: 'flex',
      alignItems: 'center',
      margin: '28px 0',
    },
    dividerLine: {
      flex: 1,
      height: '1px',
      backgroundColor: '#e5e7eb',
    },
    dividerText: {
      padding: '0 20px',
      fontSize: '13px',
      fontWeight: '600',
      color: '#9ca3af',
    },
    socialButton: {
      width: '100%',
      padding: '14px 20px',
      fontSize: '15px',
      fontWeight: '600',
      color: '#374151',
      backgroundColor: '#ffffff',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      marginBottom: '12px',
    },
    rightSide: {
      flex: '1',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    backgroundImage: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: 'url("https://images.unsplash.com/photo-1555854877-bab0e564b8d5?q=80&w=2069")',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      filter: 'brightness(0.7)',
    },
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, rgba(43,103,119,0.9) 0%, rgba(82,171,152,0.8) 100%)',
    },
    rightContent: {
      position: 'relative',
      zIndex: 2,
      padding: '60px',
      color: 'white',
      textAlign: 'center',
    },
    heroText: {
      fontSize: '48px',
      fontWeight: '900',
      marginBottom: '24px',
      textShadow: '0 4px 20px rgba(0,0,0,0.2)',
    },
    heroSubtext: {
      fontSize: '18px',
      fontWeight: '500',
      opacity: 0.95,
      lineHeight: '1.6',
      maxWidth: '500px',
      margin: '0 auto',
    },
    passwordContainer: {
      position: 'relative',
    },
    eyeIcon: {
      position: 'absolute',
      right: '18px',
      top: '50%',
      transform: 'translateY(-50%)',
      cursor: 'pointer',
      fontSize: '20px',
      color: '#6b7280',
      userSelect: 'none',
    },
    backButton: {
      background: 'transparent',
      border: 'none',
      color: '#2b6777',
      fontSize: '15px',
      fontWeight: '700',
      cursor: 'pointer',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 0',
    },
    homeButton: {
      position: 'absolute',
      top: '40px',
      left: '40px',
      padding: '12px 24px',
      background: 'rgba(43,103,119,0.1)',
      border: '2px solid rgba(82,171,152,0.3)',
      borderRadius: '12px',
      color: '#2b6777',
      fontSize: '15px',
      fontWeight: '700',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
      zIndex: 10,
    },
  };

  const styles = isMobile ? mobileStyles : desktopStyles;

  // ==========================================
  // MOBILE RENDER
  // ==========================================

  if (isMobile) {
    return (
      <div style={styles.container}>
        {/* Go Back to Home Button */}
        <button
          onClick={handleGoToHome}
          style={styles.homeButton}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(43,103,119,0.2)';
            e.target.style.borderColor = '#52ab98';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(43,103,119,0.1)';
            e.target.style.borderColor = 'rgba(82,171,152,0.3)';
          }}
        >
          <span>🏠</span>
          <span>Home</span>
        </button>

        <div style={styles.loginBox}>
          {/* Error Message */}
          {error && <div style={styles.errorMessage}>{error}</div>}

          {/* Success Message */}
          {successMessage && <div style={styles.successMessage}>{successMessage}</div>}

          {/* Email/Password - Initial */}
          {step === 'initial' && authMethod === 'email' && (
            <>
              <h1 style={styles.heading}>Welcome Back</h1>
              <p style={styles.subtitle}>Sign in with your email & password</p>

              <div style={styles.formGroup}>
                <label style={styles.label}>Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearError();
                  }}
                  onKeyPress={(e) => handleKeyPress(e, handleEmailPasswordLogin)}
                  style={styles.input}
                  placeholder="your.email@example.com"
                  onFocus={(e) => e.target.style.border = '2px solid #52ab98'}
                  onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
                  disabled={loading}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Password</label>
                <div style={styles.passwordContainer}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      clearError();
                    }}
                    onKeyPress={(e) => handleKeyPress(e, handleEmailPasswordLogin)}
                    style={styles.input}
                    placeholder="Enter your password"
                    onFocus={(e) => e.target.style.border = '2px solid #52ab98'}
                    onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
                    disabled={loading}
                  />
                  <span
                    style={styles.eyeIcon}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </span>
                </div>
              </div>

              <button
                onClick={handleEmailPasswordLogin}
                style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
                disabled={loading}
              >
                {loading ? '🔄 Logging in...' : '🔐 Sign In'}
              </button>

              <p style={{ ...styles.linkText, marginTop: '16px', marginBottom: '16px' }}>
                <span style={styles.link} onClick={handleForgotPassword}>
                  🔑 Forgot Password?
                </span>
              </p>

              <div style={styles.divider}>
                <div style={styles.dividerLine}></div>
                <span style={styles.dividerText}>OR</span>
                <div style={styles.dividerLine}></div>
              </div>

              <button
                onClick={handleSwitchToPhone}
                style={styles.socialButton}
              >
                <svg width="20" height="20" fill="#2b6777" viewBox="0 0 24 24">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                </svg>
                Login with Phone OTP
              </button>

              <p style={styles.linkText}>
                Don't have an account?{' '}
                <span style={styles.link} onClick={handleGoToRegister}>
                  Register Now
                </span>
              </p>

              <p style={{ ...styles.linkText, marginTop: '12px' }}>
                <span style={styles.link} onClick={handleCheckApplicationStatus}>
                  📋 Check Application Status
                </span>
              </p>
            </>
          )}

          {/* Phone OTP - Initial */}
          {step === 'initial' && authMethod === 'phone' && (
            <>
              <h1 style={styles.heading}>Welcome Back</h1>
              <p style={styles.subtitle}>Sign in with your phone number</p>

              <div style={styles.formGroup}>
                <label style={styles.label}>Phone Number</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '15px', fontWeight: '600', color: '#374151' }}>+91</span>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 10) {
                        setPhoneNumber(value);
                        clearError();
                      }
                    }}
                    onKeyPress={(e) => handleKeyPress(e, handleSendOTP)}
                    style={{ ...styles.input, flex: 1 }}
                    placeholder="Phone Number"
                    maxLength="10"
                    onFocus={(e) => e.target.style.border = '2px solid #52ab98'}
                    onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
                    disabled={loading}
                  />
                </div>
              </div>

              <button
                onClick={handleSendOTP}
                style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
                disabled={loading}
              >
                {loading ? '📱 Sending OTP...' : '📱 Send OTP'}
              </button>

              <div style={styles.divider}>
                <div style={styles.dividerLine}></div>
                <span style={styles.dividerText}>OR</span>
                <div style={styles.dividerLine}></div>
              </div>

              <button
                onClick={handleSwitchToEmail}
                style={styles.socialButton}
              >
                <svg width="20" height="20" fill="#2b6777" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
                Continue with Email
              </button>

              <p style={styles.linkText}>
                Don't have an account?{' '}
                <span style={styles.link} onClick={handleGoToRegister}>
                  Register Now
                </span>
              </p>

              <p style={{ ...styles.linkText, marginTop: '12px' }}>
                <span style={styles.link} onClick={handleCheckApplicationStatus}>
                  📋 Check Application Status
                </span>
              </p>
            </>
          )}

          {/* Phone OTP - Verification */}
          {step === 'otp-verification' && (
            <>
              <button onClick={handleBackToInitial} style={styles.backButton}>
                ← Back
              </button>

              <h1 style={styles.heading}>Verify OTP</h1>
              <p style={styles.subtitle}>
                Enter the code sent to +91 {phoneNumber}
              </p>

              <div style={styles.formGroup}>
                <label style={styles.label}>OTP Code</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 6) {
                      setOtp(value);
                      clearError();
                    }
                  }}
                  onKeyPress={(e) => handleKeyPress(e, handleVerifyOTP)}
                  style={{ ...styles.input, letterSpacing: '8px', textAlign: 'center', fontSize: '20px', fontWeight: '600' }}
                  placeholder="000000"
                  maxLength="6"
                  onFocus={(e) => e.target.style.border = '2px solid #52ab98'}
                  onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
                  disabled={loading}
                />
              </div>

              <button
                onClick={handleVerifyOTP}
                style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
                disabled={loading}
              >
                {loading ? '🔄 Verifying...' : '✓ Verify OTP'}
              </button>

              <p style={{ ...styles.linkText, marginTop: '16px' }}>
                {resendTimer > 0 ? (
                  <span style={{ color: '#9ca3af' }}>Resend OTP in {resendTimer}s</span>
                ) : (
                  <>
                    Didn't receive the code?{' '}
                    <span
                      style={{
                        ...styles.link,
                        opacity: loading ? 0.5 : 1,
                        cursor: loading ? 'not-allowed' : 'pointer'
                      }}
                      onClick={() => !loading && handleSendOTP()}
                    >
                      Resend OTP
                    </span>
                  </>
                )}
              </p>
            </>
          )}

          {/* Forgot Password - Enter Phone Number */}
          {step === 'forgot-password' && (
            <>
              <button onClick={handleBackToInitial} style={styles.backButton}>
                ← Back
              </button>

              <h1 style={styles.heading}>Forgot Password</h1>
              <p style={styles.subtitle}>
                Enter your phone number to receive OTP
              </p>

              <div style={styles.formGroup}>
                <label style={styles.label}>Phone Number</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '15px', fontWeight: '600', color: '#374151' }}>+91</span>
                  <input
                    type="tel"
                    value={forgotPhoneNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 10) {
                        setForgotPhoneNumber(value);
                        clearError();
                      }
                    }}
                    onKeyPress={(e) => handleKeyPress(e, handleSendForgotPasswordOTP)}
                    style={{ ...styles.input, flex: 1 }}
                    placeholder="Phone Number"
                    maxLength="10"
                    onFocus={(e) => e.target.style.border = '2px solid #52ab98'}
                    onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
                    disabled={loading}
                  />
                </div>
              </div>

              <button
                onClick={handleSendForgotPasswordOTP}
                style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
                disabled={loading}
              >
                {loading ? '📱 Sending OTP...' : '📱 Send OTP'}
              </button>
            </>
          )}

          {/* Forgot Password - Verify OTP */}
          {step === 'forgot-otp-verification' && (
            <>
              <button onClick={() => setStep('forgot-password')} style={styles.backButton}>
                ← Back
              </button>

              <h1 style={styles.heading}>Verify OTP</h1>
              <p style={styles.subtitle}>
                Enter the code sent to +91 {forgotPhoneNumber}
              </p>

              <div style={styles.formGroup}>
                <label style={styles.label}>OTP Code</label>
                <input
                  type="text"
                  value={forgotOtp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 6) {
                      setForgotOtp(value);
                      clearError();
                    }
                  }}
                  onKeyPress={(e) => handleKeyPress(e, handleVerifyForgotPasswordOTP)}
                  style={{ ...styles.input, letterSpacing: '8px', textAlign: 'center', fontSize: '20px', fontWeight: '600' }}
                  placeholder="000000"
                  maxLength="6"
                  onFocus={(e) => e.target.style.border = '2px solid #52ab98'}
                  onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
                  disabled={loading}
                />
              </div>

              <button
                onClick={handleVerifyForgotPasswordOTP}
                style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
                disabled={loading}
              >
                {loading ? '🔄 Verifying...' : '✓ Verify OTP'}
              </button>

              <p style={{ ...styles.linkText, marginTop: '16px' }}>
                {resendTimer > 0 ? (
                  <span style={{ color: '#9ca3af' }}>Resend OTP in {resendTimer}s</span>
                ) : (
                  <>
                    Didn't receive the code?{' '}
                    <span
                      style={{
                        ...styles.link,
                        opacity: loading ? 0.5 : 1,
                        cursor: loading ? 'not-allowed' : 'pointer'
                      }}
                      onClick={() => !loading && handleSendForgotPasswordOTP()}
                    >
                      Resend OTP
                    </span>
                  </>
                )}
              </p>
            </>
          )}

          {/* Reset Password */}
          {step === 'reset-password' && (
            <>
              <button onClick={handleBackToInitial} style={styles.backButton}>
                ← Back to Login
              </button>

              <h1 style={styles.heading}>Reset Password</h1>
              <p style={styles.subtitle}>
                Create a new password for your account
              </p>

              <div style={styles.formGroup}>
                <label style={styles.label}>New Password</label>
                <div style={styles.passwordContainer}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      clearError();
                    }}
                    style={styles.input}
                    placeholder="Enter new password"
                    onFocus={(e) => e.target.style.border = '2px solid #52ab98'}
                    onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
                    disabled={loading}
                  />
                  <span
                    style={styles.eyeIcon}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? '👁️' : '👁️‍🗨️'}
                  </span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Confirm Password</label>
                <div style={styles.passwordContainer}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      clearError();
                    }}
                    onKeyPress={(e) => handleKeyPress(e, handleResetPassword)}
                    style={styles.input}
                    placeholder="Confirm new password"
                    onFocus={(e) => e.target.style.border = '2px solid #52ab98'}
                    onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
                    disabled={loading}
                  />
                  <span
                    style={styles.eyeIcon}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                  </span>
                </div>
              </div>

              <button
                onClick={handleResetPassword}
                style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
                disabled={loading}
              >
                {loading ? '🔄 Resetting...' : '🔑 Reset Password'}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ==========================================
  // DESKTOP RENDER
  // ==========================================

  return (
    <div style={desktopStyles.container}>
      {/* Go Back to Home Button */}
      <button
        onClick={handleGoToHome}
        style={desktopStyles.homeButton}
        onMouseEnter={(e) => {
          e.target.style.background = 'rgba(43,103,119,0.2)';
          e.target.style.borderColor = '#52ab98';
          e.target.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = 'rgba(43,103,119,0.1)';
          e.target.style.borderColor = 'rgba(82,171,152,0.3)';
          e.target.style.transform = 'translateY(0)';
        }}
      >
        <span>🏠</span>
        <span>Home</span>
      </button>

      <div style={desktopStyles.leftSide}>
        <div style={desktopStyles.leftContent}>
          {/* Error Message */}
          {error && <div style={desktopStyles.errorMessage}>{error}</div>}

          {/* Success Message */}
          {successMessage && <div style={desktopStyles.successMessage}>{successMessage}</div>}

          {/* Email/Password - Initial */}
          {step === 'initial' && authMethod === 'email' && (
            <>
              <h1 style={desktopStyles.heading}>Welcome Back</h1>
              <p style={desktopStyles.subtitle}>Sign in with your email & password</p>

              <div style={desktopStyles.formGroup}>
                <label style={desktopStyles.label}>Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearError();
                  }}
                  onKeyPress={(e) => handleKeyPress(e, handleEmailPasswordLogin)}
                  style={desktopStyles.input}
                  placeholder="your.email@example.com"
                  onFocus={(e) => e.target.style.border = '2px solid #52ab98'}
                  onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
                  disabled={loading}
                />
              </div>

              <div style={desktopStyles.formGroup}>
                <label style={desktopStyles.label}>Password</label>
                <div style={desktopStyles.passwordContainer}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      clearError();
                    }}
                    onKeyPress={(e) => handleKeyPress(e, handleEmailPasswordLogin)}
                    style={desktopStyles.input}
                    placeholder="Enter your password"
                    onFocus={(e) => e.target.style.border = '2px solid #52ab98'}
                    onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
                    disabled={loading}
                  />
                  <span
                    style={desktopStyles.eyeIcon}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </span>
                </div>
              </div>

              <button
                onClick={handleEmailPasswordLogin}
                style={{ ...desktopStyles.button, ...(loading ? desktopStyles.buttonDisabled : {}) }}
                onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                disabled={loading}
              >
                {loading ? '🔄 Logging in...' : '🔐 Sign In'}
              </button>

              <p style={{ ...desktopStyles.linkText, marginTop: '16px', marginBottom: '16px' }}>
                <span style={desktopStyles.link} onClick={handleForgotPassword}>
                  🔑 Forgot Password?
                </span>
              </p>

              <div style={desktopStyles.divider}>
                <div style={desktopStyles.dividerLine}></div>
                <span style={desktopStyles.dividerText}>OR</span>
                <div style={desktopStyles.dividerLine}></div>
              </div>

              <button
                onClick={handleSwitchToPhone}
                style={desktopStyles.socialButton}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#ffffff'}
              >
                <svg width="20" height="20" fill="#2b6777" viewBox="0 0 24 24">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                </svg>
                Login with Phone OTP
              </button>

              <p style={desktopStyles.linkText}>
                Don't have an account?{' '}
                <span style={desktopStyles.link} onClick={handleGoToRegister}>
                  Register Now
                </span>
              </p>

              <p style={{ ...desktopStyles.linkText, marginTop: '12px' }}>
                <span style={desktopStyles.link} onClick={handleCheckApplicationStatus}>
                  📋 Check Application Status
                </span>
              </p>
            </>
          )}

          {/* Phone OTP - Initial */}
          {step === 'initial' && authMethod === 'phone' && (
            <>
              <h1 style={desktopStyles.heading}>Welcome Back</h1>
              <p style={desktopStyles.subtitle}>Sign in with your phone number</p>

              <div style={desktopStyles.formGroup}>
                <label style={desktopStyles.label}>Phone Number</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '15px', fontWeight: '600', color: '#374151' }}>+91</span>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 10) {
                        setPhoneNumber(value);
                        clearError();
                      }
                    }}
                    onKeyPress={(e) => handleKeyPress(e, handleSendOTP)}
                    style={{ ...desktopStyles.input, flex: 1 }}
                    placeholder="Phone Number"
                    maxLength="10"
                    onFocus={(e) => e.target.style.border = '2px solid #52ab98'}
                    onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
                    disabled={loading}
                  />
                </div>
              </div>

              <button
                onClick={handleSendOTP}
                style={{ ...desktopStyles.button, ...(loading ? desktopStyles.buttonDisabled : {}) }}
                onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                disabled={loading}
              >
                {loading ? '📱 Sending OTP...' : '📱 Send OTP'}
              </button>

              <div style={desktopStyles.divider}>
                <div style={desktopStyles.dividerLine}></div>
                <span style={desktopStyles.dividerText}>OR</span>
                <div style={desktopStyles.dividerLine}></div>
              </div>

              <button
                onClick={handleSwitchToEmail}
                style={desktopStyles.socialButton}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#ffffff'}
              >
                <svg width="20" height="20" fill="#2b6777" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
                Continue with Email
              </button>

              <p style={desktopStyles.linkText}>
                Don't have an account?{' '}
                <span style={desktopStyles.link} onClick={handleGoToRegister}>
                  Register Now
                </span>
              </p>

              <p style={{ ...desktopStyles.linkText, marginTop: '12px' }}>
                <span style={desktopStyles.link} onClick={handleCheckApplicationStatus}>
                  📋 Check Application Status
                </span>
              </p>
            </>
          )}

          {/* Phone OTP - Verification */}
          {step === 'otp-verification' && (
            <>
              <button onClick={handleBackToInitial} style={desktopStyles.backButton}>
                ← Back
              </button>

              <h1 style={desktopStyles.heading}>Verify OTP</h1>
              <p style={desktopStyles.subtitle}>
                Enter the code sent to +91 {phoneNumber}
              </p>

              <div style={desktopStyles.formGroup}>
                <label style={desktopStyles.label}>OTP Code</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 6) {
                      setOtp(value);
                      clearError();
                    }
                  }}
                  onKeyPress={(e) => handleKeyPress(e, handleVerifyOTP)}
                  style={{ ...desktopStyles.input, letterSpacing: '8px', textAlign: 'center', fontSize: '20px', fontWeight: '600' }}
                  placeholder="000000"
                  maxLength="6"
                  onFocus={(e) => e.target.style.border = '2px solid #52ab98'}
                  onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
                  disabled={loading}
                />
              </div>

              <button
                onClick={handleVerifyOTP}
                style={{ ...desktopStyles.button, ...(loading ? desktopStyles.buttonDisabled : {}) }}
                onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                disabled={loading}
              >
                {loading ? '🔄 Verifying...' : '✓ Verify OTP'}
              </button>

              <p style={{ ...desktopStyles.linkText, marginTop: '16px' }}>
                {resendTimer > 0 ? (
                  <span style={{ color: '#9ca3af' }}>Resend OTP in {resendTimer}s</span>
                ) : (
                  <>
                    Didn't receive the code?{' '}
                    <span
                      style={{
                        ...desktopStyles.link,
                        opacity: loading ? 0.5 : 1,
                        cursor: loading ? 'not-allowed' : 'pointer'
                      }}
                      onClick={() => !loading && handleSendOTP()}
                    >
                      Resend OTP
                    </span>
                  </>
                )}
              </p>
            </>
          )}

          {/* Forgot Password - Enter Phone Number */}
          {step === 'forgot-password' && (
            <>
              <button onClick={handleBackToInitial} style={desktopStyles.backButton}>
                ← Back
              </button>

              <h1 style={desktopStyles.heading}>Forgot Password</h1>
              <p style={desktopStyles.subtitle}>
                Enter your phone number to receive OTP
              </p>

              <div style={desktopStyles.formGroup}>
                <label style={desktopStyles.label}>Phone Number</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '15px', fontWeight: '600', color: '#374151' }}>+91</span>
                  <input
                    type="tel"
                    value={forgotPhoneNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 10) {
                        setForgotPhoneNumber(value);
                        clearError();
                      }
                    }}
                    onKeyPress={(e) => handleKeyPress(e, handleSendForgotPasswordOTP)}
                    style={{ ...desktopStyles.input, flex: 1 }}
                    placeholder="Phone Number"
                    maxLength="10"
                    onFocus={(e) => e.target.style.border = '2px solid #52ab98'}
                    onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
                    disabled={loading}
                  />
                </div>
              </div>

              <button
                onClick={handleSendForgotPasswordOTP}
                style={{ ...desktopStyles.button, ...(loading ? desktopStyles.buttonDisabled : {}) }}
                onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                disabled={loading}
              >
                {loading ? '📱 Sending OTP...' : '📱 Send OTP'}
              </button>
            </>
          )}

          {/* Forgot Password - Verify OTP */}
          {step === 'forgot-otp-verification' && (
            <>
              <button onClick={() => setStep('forgot-password')} style={desktopStyles.backButton}>
                ← Back
              </button>

              <h1 style={desktopStyles.heading}>Verify OTP</h1>
              <p style={desktopStyles.subtitle}>
                Enter the code sent to +91 {forgotPhoneNumber}
              </p>

              <div style={desktopStyles.formGroup}>
                <label style={desktopStyles.label}>OTP Code</label>
                <input
                  type="text"
                  value={forgotOtp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 6) {
                      setForgotOtp(value);
                      clearError();
                    }
                  }}
                  onKeyPress={(e) => handleKeyPress(e, handleVerifyForgotPasswordOTP)}
                  style={{ ...desktopStyles.input, letterSpacing: '8px', textAlign: 'center', fontSize: '20px', fontWeight: '600' }}
                  placeholder="000000"
                  maxLength="6"
                  onFocus={(e) => e.target.style.border = '2px solid #52ab98'}
                  onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
                  disabled={loading}
                />
              </div>

              <button
                onClick={handleVerifyForgotPasswordOTP}
                style={{ ...desktopStyles.button, ...(loading ? desktopStyles.buttonDisabled : {}) }}
                onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                disabled={loading}
              >
                {loading ? '🔄 Verifying...' : '✓ Verify OTP'}
              </button>

              <p style={{ ...desktopStyles.linkText, marginTop: '16px' }}>
                {resendTimer > 0 ? (
                  <span style={{ color: '#9ca3af' }}>Resend OTP in {resendTimer}s</span>
                ) : (
                  <>
                    Didn't receive the code?{' '}
                    <span
                      style={{
                        ...desktopStyles.link,
                        opacity: loading ? 0.5 : 1,
                        cursor: loading ? 'not-allowed' : 'pointer'
                      }}
                      onClick={() => !loading && handleSendForgotPasswordOTP()}
                    >
                      Resend OTP
                    </span>
                  </>
                )}
              </p>
            </>
          )}

          {/* Reset Password */}
          {step === 'reset-password' && (
            <>
              <button onClick={handleBackToInitial} style={desktopStyles.backButton}>
                ← Back to Login
              </button>

              <h1 style={desktopStyles.heading}>Reset Password</h1>
              <p style={desktopStyles.subtitle}>
                Create a new password for your account
              </p>

              <div style={desktopStyles.formGroup}>
                <label style={desktopStyles.label}>New Password</label>
                <div style={desktopStyles.passwordContainer}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      clearError();
                    }}
                    style={desktopStyles.input}
                    placeholder="Enter new password"
                    onFocus={(e) => e.target.style.border = '2px solid #52ab98'}
                    onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
                    disabled={loading}
                  />
                  <span
                    style={desktopStyles.eyeIcon}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? '👁️' : '👁️‍🗨️'}
                  </span>
                </div>
              </div>

              <div style={desktopStyles.formGroup}>
                <label style={desktopStyles.label}>Confirm Password</label>
                <div style={desktopStyles.passwordContainer}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      clearError();
                    }}
                    onKeyPress={(e) => handleKeyPress(e, handleResetPassword)}
                    style={desktopStyles.input}
                    placeholder="Confirm new password"
                    onFocus={(e) => e.target.style.border = '2px solid #52ab98'}
                    onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
                    disabled={loading}
                  />
                  <span
                    style={desktopStyles.eyeIcon}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                  </span>
                </div>
              </div>

              <button
                onClick={handleResetPassword}
                style={{ ...desktopStyles.button, ...(loading ? desktopStyles.buttonDisabled : {}) }}
                onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                disabled={loading}
              >
                {loading ? '🔄 Resetting...' : '🔑 Reset Password'}
              </button>
            </>
          )}
        </div>
      </div>

      <div style={desktopStyles.rightSide}>
        <div style={desktopStyles.backgroundImage}></div>
        <div style={desktopStyles.overlay}></div>
        <div style={desktopStyles.rightContent}>
          <h2 style={desktopStyles.heroText}>
            Learn, Grow, Succeed
          </h2>
          <p style={desktopStyles.heroSubtext}>
            Join thousands of students on their journey to academic excellence and personal growth in our modern hostel facilities.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ResponsiveLoginPage;